import { isPlatformBrowser, Location as NgLocation } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, DOCUMENT, signal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import Intercom from '@intercom/messenger-js-sdk';
import {
  ASSISTED_BOOKING_SESSION_KEY,
  BookingStoreService
} from 'avn/modules/features/booking-flow/booking-store.service';
import { ApiService } from 'avn/modules/services/api.service';
import { AuthAppService } from 'avn/modules/services/auth-app/auth-app.service';
import { ClarityService } from 'avn/modules/services/clarity.service';
import { Constants } from 'avn/modules/services/constants';
import { GoogleMapsLoaderService } from 'avn/modules/services/google-maps-loader.service';
import { MaintenanceService } from 'avn/modules/services/maintenance.service';
import { SharedCompsService } from 'avn/modules/services/shared-comps.service';
import { StoreService } from 'avn/modules/services/store.service';
import { FooterComponent } from 'avn/modules/shared/components/footer/footer';
import { HeaderComponent } from 'avn/modules/shared/components/header/header';
import { MaintenanceScrollerComponent } from 'avn/modules/shared/components/maintenance-scroller/maintenance-scroller';
import { SchemaService } from 'avn/modules/shared/components/seo-extention/schema.service';
import { SeoExtentionComponent } from 'avn/modules/shared/components/seo-extention/seo-extention';
import { SnackbarComponent } from 'avn/modules/shared/components/snackbar/snackbar';
import { Location } from 'avn/modules/shared/interfaces/homepage';
import { User } from 'avn/modules/shared/interfaces/user';
import { CanonicalUrlPipe } from 'avn/modules/shared/pipes/canonical-url.pipe';
import { SlugTextPipe } from 'avn/modules/shared/pipes/slug-text.pipe';
import moment from 'moment';
import { Subject, Subscription, interval, merge } from 'rxjs';
import { startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  imports: [HeaderComponent, MaintenanceScrollerComponent, RouterOutlet, FooterComponent, SnackbarComponent]
})
export class AppComponent extends SeoExtentionComponent implements OnInit, OnDestroy {
  public workcoverLoggedUser!: User;

  public readonly showFooter = signal(true);
  public readonly showHeader = signal(true);
  public userEmail = '';
  public name = 'Anonymous User';
  public platform = Constants.APP_NAME;
  public hmacHash = '';
  public previousUrl = '';
  public currentUrl = '';
  private readonly subscriptions: Subscription[] = [];

  public constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthAppService,
    private readonly api: ApiService,
    public store: StoreService,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly titleService: Title,
    private readonly metaService: Meta,
    @Inject(DOCUMENT) private readonly dom_: any,
    private readonly schema: SchemaService,
    private readonly slugPipe: SlugTextPipe,
    private readonly canonical_: CanonicalUrlPipe,
    private readonly booking_store: BookingStoreService,
    private readonly shared: SharedCompsService,
    private readonly maintenanceService: MaintenanceService,
    private readonly clarityService: ClarityService,
    private readonly ngLocation: NgLocation,
    private readonly googleMapsLoader: GoogleMapsLoaderService
  ) {
    super(router, route, titleService, metaService, dom_, schema, slugPipe, canonical_, store);
  }

  private readonly destroy$ = new Subject<void>();
  private readonly maintenanceStop$ = new Subject<void>();

  public ngOnInit(): void {
    super.initialCall();
    this.initBranding();
    this.updateLayoutVisibility(this.ngLocation.path(true));

    if (isPlatformBrowser(this.platformId)) {
      this.handleMaintenanceMode();
      const fetchMeraLocationSub = this.store.fetchmeraLocation.subscribe(() => {
        this.getmeraLocation();
      });
      this.subscriptions.push(fetchMeraLocationSub);

      const meraLocationSub = this.store.meraLocationSub.subscribe((resp) => {
        if (!resp.post_code) {
          this.getmeraLocation();
        }
        const regex = new RegExp(/^(0[289][0-9]{2})|([1-9][0-9]{3})$/);
        if (resp.post_code && !regex.test(resp.post_code)) {
          resp.city_name = 'Sydney';
          resp.post_code = '2000';
          resp.lng = '151.2092955';
          resp.lat = '-33.8688197';
          resp.state_name = 'New South Wales';
          this.store.setmeraLocation(resp);
        }
      });
      this.subscriptions.push(meraLocationSub);

      this.getworkcoverLoggedUser();
      const workcoverLoggedUserSub = this.authService.workcoverLoggedUser.subscribe({
        next: (user: any) => {
          if (user?.email) {
            this.userEmail = user.email || '';
            this.name = user.name || '';
          }
        }
      });
      this.subscriptions.push(workcoverLoggedUserSub);

      if (this.userEmail) {
        this.hmacHash = this.store.generateHmac(this.userEmail, Constants.INTERCOM_CONFIG.secretKey);
      }
      // For anonymous users, no email or HMAC needed - Intercom automation will handle email collection
      this.inItIntercom();
      const routerEventsSub = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // Always fall back to empty strings
          this.previousUrl = this.currentUrl || '';
          this.currentUrl = event.urlAfterRedirects || '';

          // Guard against undefined before using .includes()
          const tempBookingSlug = this.getTemporarySlugForCancel();
          if (
            (this.previousUrl || '').includes('/checkout') &&
            !(this.currentUrl || '').includes('/confirm/') &&
            tempBookingSlug
          ) {
            this.api.cancelTempAppointment(tempBookingSlug).subscribe({
              next: () => {
                this.shared.showSnackBar('Temporary appointment cancelled', 3);
              },
              error: () => {
                this.shared.showSnackBar('Failed to cancel temporary appointment', 3);
              }
            });
          }

          setTimeout(() => {
            const contentContainer: any = document.querySelector('body') || window;
            // optional: use a valid options object
            contentContainer?.scrollIntoView?.({ block: 'start' });
          }, 300);

          // Use a safe URL string for splitting
          const safeUrl = event.urlAfterRedirects || event.url || '';
          const url_split = safeUrl.split('/');
          if (url_split[1] === 'assistedbooking') {
            sessionStorage.setItem(ASSISTED_BOOKING_SESSION_KEY, 'true');
          } else if (url_split[1] === 'browse' || url_split[1] === 'search') {
            sessionStorage.removeItem(ASSISTED_BOOKING_SESSION_KEY);
          }
          this.updateLayoutVisibility(safeUrl);
        }
      });
      this.clarityService.initClarity();
      this.subscriptions.push(routerEventsSub);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.destroy$.next();
    this.destroy$.complete();
    this.maintenanceStop$.next();
    this.maintenanceStop$.complete();
  }

  private updateLayoutVisibility(url: string): void {
    const url_split = (url || '').split('/');
    this.showFooter.set(
      ![
        'search',
        'browse',
        'assistedbooking',
        'business',
        'wellbeing',
        'booking',
        'checkout',
        'confirm',
        'profile',
        'link',
        'maintenance'
      ].includes(url_split[1])
    );
    this.showHeader.set(!['booking', 'checkout', 'confirm', 'maintenance'].includes(url_split[1]));
  }

  private isValidTemporarySlug(slug: string | undefined | null): boolean {
    return !!slug && slug !== 'undefined' && slug !== 'null';
  }

  private getTemporarySlugForCancel(): string {
    const storeSlug = this.booking_store.bookingTemporarySlug;
    if (this.isValidTemporarySlug(storeSlug)) {
      return storeSlug;
    }

    const previousPath = (this.previousUrl || '').split('?')[0];
    const match = previousPath.match(/^\/booking\/([^/]+)\/checkout(?:\/)?$/);
    return match?.[1] && this.isValidTemporarySlug(match[1]) ? match[1] : '';
  }

  public inItIntercom() {
    if (this.userEmail) {
      // Logged-in user with email and HMAC
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: this.name,
        email: this.userEmail,
        platform: this.platform,
        user_hash: this.hmacHash
      });
    } else {
      // Anonymous user - no email, let Intercom automation ask for it
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: `Anonymous ${this.platform} User`,
        platform: this.platform
      });
    }
  }

  public getworkcoverLoggedUser() {
    try {
      const lastLogin = localStorage.getItem('workCoverLastLogin');
      if (lastLogin) {
        // Calculate the difference in hours
        const differenceInHours = moment().diff(moment(new Date(lastLogin)), 'hours');
        if (differenceInHours >= 24) {
          this.authService.purgeAuth();
        } else {
          this.fetchUserData();
        }
      } else {
        this.fetchUserData();
      }
    } catch (error) {
      console.info('local storage error: IGNORE', error);
    }
  }

  public fetchUserData() {
    const user = localStorage.getItem('workcoverLoggedUser');
    if (!user) {
      return;
    }
    this.authService.setworkcoverLoggedUser(JSON.parse(user));
  }

  public getmeraLocation() {
    // Check if geolocation is supported by the browser
    if (isPlatformBrowser(this.platformId) && 'geolocation' in window.navigator) {
      // Prompt user for permission to access their location
      window.navigator.geolocation.getCurrentPosition(
        // Success callback function
        (position) => {
          // Get the user's latitude and longitude coordinates
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          // const lat = '-37.8156954';
          // const lng = '144.8828322';

          this.getCoordinates(lat, lng);

          // Do something with the location data, e.g. display on a map
        },
        // Error callback function
        (error) => {
          // Handle errors, e.g. user denied location sharing permissions
          console.error('Error getting user location:', error);
        }
      );
    } else {
      // Geolocation is not supported by the browser
      console.error('Geolocation is not supported by this browser.');
      const lat = '-33.8688197';
      const lng = '151.2092955';

      this.getCoordinates(lat, lng);
    }
  }

  public getCoordinates(latitude: string | number, longitude: string | number) {
    const lat_lon = latitude + ', ' + longitude;
    this.api.getGeoCodingByAddress(lat_lon).subscribe({
      next: (response: any) => {
        let post_code = '2000';
        let state_name = 'New South Wales';
        let city_name = 'Sydeney';
        const list_with_post_code = response.geocode_details.results.find(
          (geocode: any) => geocode.address_components.findIndex((x: any) => x.types.indexOf('postal_code') > -1) > -1
        );

        let addresscomponents = [];
        if (list_with_post_code?.address_components) {
          addresscomponents = [...list_with_post_code.address_components];
        } else {
          addresscomponents = [...response.geocode_details.results[0].address_components];
        }

        for (let i = 0; i < addresscomponents.length; i++) {
          const component = addresscomponents[i];
          if (component.types.indexOf('postal_code') > -1) {
            post_code = component.long_name;
          } else if (component.types.indexOf('administrative_area_level_1') > -1) {
            state_name = component.long_name;
          } else if (
            component.types.indexOf('locality') > -1 ||
            component.types.indexOf('sublocality') > -1 ||
            component.types.indexOf('neighborhood') > -1
          ) {
            city_name = component.long_name;
          }
        }
        const location = new Location();
        location.lat = latitude;
        location.lng = longitude;
        location.post_code = post_code;
        location.city_name = city_name;
        location.state_name = state_name;
        this.store.setmeraLocation(location);
      }
    });
  }

  private initBranding(): void {
    this.titleService.setTitle(Constants.BRANDING.title);
    this.metaService.updateTag({ name: 'theme-color', content: Constants.BRANDING.themeColor });

    const favicon = this.dom_.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.setAttribute('href', Constants.BRANDING.faviconAsset);
    }

    if (isPlatformBrowser(this.platformId)) {
      this.injectGoogleTagManager();
      this.injectGoogleMapsScript();
    }
  }

  private injectGoogleTagManager(): void {
    const gtmId = environment.gtmId;
    if (!gtmId) {
      return;
    }

    const script = this.dom_.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');`;
    this.dom_.head.appendChild(script);

    const iframe = this.dom_.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    const noscript = this.dom_.createElement('noscript');
    noscript.appendChild(iframe);
    this.dom_.body.insertBefore(noscript, this.dom_.body.firstChild);
  }

  private injectGoogleMapsScript(): void {
    this.googleMapsLoader.load(this.dom_).catch((err) => {
      // Loading can legitimately fail client-side (e.g. an ad blocker/privacy
      // extension blocking Google's script). Places-dependent components guard
      // on `whenReady()` themselves, so just log it rather than treating it as
      // an app error.
      console.info('[GoogleMapsLoader] Failed to load Google Maps script:', err);
    });
  }

  private handleMaintenanceMode() {
    interval(60000)
      .pipe(
        startWith(0),
        switchMap(() => this.maintenanceService.getMaintenanceConfig().pipe(take(1))),
        takeUntil(merge(this.destroy$, this.maintenanceStop$))
      )
      .subscribe((config) => {
        this.store.maintenanceConfig = config;
        if (!config.repeat) {
          this.maintenanceStop$.next();
        }
        const isActive = this.store.isMaintenanceActive(config.maintenance);
        const alreadyOnMaintenance = this.router.url.startsWith('/maintenance');

        if (isActive && !alreadyOnMaintenance) {
          this.router.navigate(['/maintenance']);
        } else if (!isActive && alreadyOnMaintenance) {
          this.router.navigate(['/']);
        }
      });
  }
}
